import { createContext, useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { CONSTANT } from '@const';
import { NotificationService } from '@data';
import { NotificationType, WaterIQUserType } from '@types';

import { UserTypeContext } from './UserTypeContextProvider';

/**
 * Notifications Context State
 **/
export interface NotificationsContextState {
	/** List of notifications */
	notifications: NotificationType[];
	/** Callback function when a notification is selected */
	setNotifications: (notifications: NotificationType[]) => void;
	unreadNumber: number;
	/** Selected notification */
	selectedNotification?: NotificationType;
	setSelectedNotification: (notification: NotificationType | undefined) => void;
	/** Get notifications query result */
	getNotifications: UseQueryResult<NotificationType[], Error>;
}

/** Default values for the Notifications Context */
const contextDefaultValues: NotificationsContextState = {
	notifications: [],
	setNotifications: () => {
		// do nothing
	},
	unreadNumber: 0,
	setSelectedNotification: () => {
		// do nothing
	},
	getNotifications: {} as UseQueryResult<NotificationType[], Error>,
};

export const NotificationsContext = createContext<NotificationsContextState>(contextDefaultValues);

/**
 * Notifications Context Provider
 * fetch notification using the API and provide the number of unread messages
 * @Component
 * @param param0
 * @returns
 */
export const NotificationsContextProvider = ({ children }: { children: React.ReactNode }) => {
	// Context
	const { selectedUserType } = useContext(UserTypeContext);
	// State
	const [notifications, setNotifications] = useState<NotificationType[]>(
		contextDefaultValues.notifications,
	);
	const [unreadNumber, setUnreadNumber] = useState<number>(contextDefaultValues.unreadNumber);
	const [selectedNotification, setSelectedNotification] = useState<NotificationType>();

	// Data
	const getNotifications = useQuery<NotificationType[], Error>({
		queryKey: [CONSTANT.NOTIFICATIONS_API_URL],
		queryFn: () => NotificationService.getNotifications(),
		enabled: selectedUserType == WaterIQUserType.WEH,
	});

	// Update notifications when data changes
	useEffect(() => {
		if (getNotifications.data) {
			setNotifications(getNotifications.data);
		}
	}, [getNotifications.data]);

	const checkOutUnreadNotifications = () => {
		if (Array.isArray(notifications)) {
			const unreadNumber = notifications.filter(notification => !notification.read).length;
			setUnreadNumber(unreadNumber);
		} else {
			setUnreadNumber(0);
		}
	};

	useEffect(() => {
		checkOutUnreadNotifications();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notifications]); // important: not add checkOutUnreadNotifications dependency

	return (
		<NotificationsContext.Provider
			value={{
				notifications,
				setNotifications,
				unreadNumber,
				selectedNotification,
				setSelectedNotification,
				getNotifications,
			}}
		>
			{children}
		</NotificationsContext.Provider>
	);
};
